<template>
  <div
    class="m-t-18 m-b-8"
    v-if="authStore.getPayloadTenant() == rootGetters.inhouse_tenant"
  >
    <div
      class="flex"
      style="width: fit-content"
      v-tooltip.bottom="{
        value: !campaign.brand ? 'Необходимо выбрать бренд' : '',
        class: 'white-tooltip-with-arrow',
        disabled: !!campaign.brand,
      }"
    >
      <label for="tech-campaign" class="m-r-20 tech-campaign-label">
        Это техническая кампания
      </label>
      <InputSwitch
        v-model="isTechCampaign"
        inputId="tech-campaign"
        :disabled="!campaign.brand || isLoadingData"
        @update:modelValue="handleTechCampaignToggle"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { useRouter } from "vue-router";
import InputSwitch from "primevue/inputswitch";
import { useCampaignStore } from "@/store/campaigns/campaigns";
import {
  isTechCampaignConditionsMet,
  prepareTechCampaignSearchParams,
  prepareTechCampaignData,
} from "@/services/campaigns/techCampaign";
import { rootGetters } from "@/main";
import { useAuthStore } from "@/store/auth/login";
import type { Campaign } from "@/store/campaigns/campaigns";

const props = defineProps<{
  campaign: Campaign;
  isLoadingData: boolean;
}>();

const emit = defineEmits<{
  (e: "update:campaign", value: Campaign): void;
  (e: "setLoading", value: boolean): void;
}>();

const router = useRouter();
const store = useCampaignStore();
const authStore = useAuthStore();
const isTechCampaign = ref(false);

watch(
  () => props.campaign,
  () => {
    if (isTechCampaign.value && !isTechCampaignConditionsMet(props.campaign)) {
      isTechCampaign.value = false;
    }
  },
  { deep: true }
);

const handleTechCampaignToggle = async (value: boolean) => {
  if (value) {
    try {
      await createOrOpenTechCampaign();
    } catch (error) {
      isTechCampaign.value = false;
    }
  } else {
    if (props.campaign.extra_info === "tech") {
      emit("update:campaign", {
        ...props.campaign,
        extra_info: "",
      });
    }
  }
};

const createOrOpenTechCampaign = async () => {
  if (!props.campaign.brand) {
    rootGetters.toast.show("warn", "Ошибка", "Необходимо выбрать бренд");
    return;
  }

  emit("setLoading", true);

  try {
    const params = prepareTechCampaignSearchParams(
      props.campaign.brand.brand_pk
    );

    await store.getCampaigns(params);

    if (store.campaignsList && store.campaignsList.length > 0) {
      const foundCampaign = store.campaignsList[0];
      rootGetters.toast.show(
        "info",
        "Информация",
        `Открыта существующая техническая кампания "${foundCampaign.name}"`
      );
      router.push({ name: "campaign", params: { id: foundCampaign.id } });
      return;
    }

    const techCampaignData = await prepareTechCampaignData();

    if (!techCampaignData) {
      const techShopId = rootGetters.tech_shop_id;
      rootGetters.toast.show(
        "warn",
        "Ошибка",
        techShopId
          ? `Магазин с id=${techShopId} не найден`
          : "Не указан shop_id в конфигурации приложения (VUE_APP_TECH_SHOP_ID)"
      );
      emit("setLoading", false);
      return;
    }

    emit("update:campaign", {
      ...props.campaign,
      shop: techCampaignData.shop,
      date_from: techCampaignData.startDate,
      date_to: techCampaignData.endDate,
      strategy: techCampaignData.strategy,
      extra_info: techCampaignData.extra_info,
    });

    isTechCampaign.value = true;

    rootGetters.toast.show(
      "info",
      "Информация",
      "Поля формы заполнены для создания технической кампании. Заполните сегменты и нажмите 'Отправить в рекламный кабинет'."
    );
  } catch (error: any) {
    rootGetters.toast.show(
      "error",
      "Ошибка",
      error.message ||
        error.response?.data?.detail ||
        "Произошла ошибка при поиске технической кампании"
    );
    isTechCampaign.value = false;
  } finally {
    emit("setLoading", false);
  }
};
</script>

<style lang="scss" scoped>
.tech-campaign-label {
  color: $main-font-color;
}
</style>
